import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, SearchResult } from '../../api/ApiClient';
import { viewOriginalDocument, downloadOriginalDocument } from '../../utils/documents/documentHelpers';
import { log } from '../../utils/errors/logger';

/**
 * Props for useDocumentTable hook
 */
export interface UseDocumentTableProps {
  /** Callback to show snackbar messages */
  onShowSnackbar: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

/**
 * Return type for useDocumentTable hook
 */
export interface UseDocumentTableReturn {
  /** Handle view document action */
  handleView: (doc: Document | SearchResult) => Promise<void>;
  /** Handle download document action */
  handleDownload: (doc: Document | SearchResult) => Promise<void>;
}

/**
 * useDocumentTable - Document table operations hook
 *
 * Custom hook that provides document table operations:
 * - View document (opens in browser/new tab)
 * - Download document (custom documents only)
 *
 * Features:
 * - Error handling with snackbar notifications
 * - SAP Discovery Center URL transformation
 * - Logging for user actions
 * - Memoized callbacks for performance
 *
 * @param onShowSnackbar - Callback to show snackbar messages
 * @returns Object with handleView and handleDownload functions
 *
 * @example
 * ```tsx
 * const { handleView, handleDownload } = useDocumentTable({
 *   onShowSnackbar: (msg, severity) => showSnackbar(msg, severity)
 * });
 *
 * <DocumentActionButtons
 *   onView={handleView}
 *   onDownload={handleDownload}
 * />
 * ```
 */
export const useDocumentTable = ({
  onShowSnackbar
}: UseDocumentTableProps): UseDocumentTableReturn => {
  const { t } = useTranslation();

  /**
   * Transform SAP Discovery Center URLs if needed
   * Converts serviceCatalog URLs to ai-feature URLs
   */
  const transformUrl = useCallback((url: string) => {
    if (url && url.includes('discovery-center.cloud.sap/serviceCatalog/')) {
      // Extract the UUID from the URL
      const uuid = url.split('/').pop();
      return `https://discovery-center.cloud.sap/protected/index.html#/ai-feature/${uuid}/`;
    }
    return url;
  }, []);

  /**
   * Handle view document action
   * - For SAP documents: Opens transformed URL in new tab
   * - For custom documents: Displays original file in browser
   */
  const handleView = useCallback(async (doc: Document | SearchResult) => {
    try {
      // For SAP documents use the normal URL, for custom documents use the original file
      if ('source_type' in doc && doc.source_type === 'sap') {
        const url = transformUrl(doc.url || '');
        window.open(url, '_blank');
        log.info('Opened SAP document in new tab', { documentId: doc.document_id, url });
      } else {
        // Display original document in browser
        await viewOriginalDocument(doc.document_id || '');
        log.info('Viewed custom document', { documentId: doc.document_id });
      }
    } catch (error) {
      log.error('Error viewing document', {
        error: error instanceof Error ? error.message : String(error),
        documentId: doc.document_id || 'unknown'
      });
      onShowSnackbar(t('documents.notification.viewError'), 'error');
    }
  }, [transformUrl, onShowSnackbar, t]);

  /**
   * Handle download document action
   * Only available for custom (non-SAP) documents
   */
  const handleDownload = useCallback(async (doc: Document | SearchResult) => {
    try {
      await downloadOriginalDocument(doc.document_id || '');
      log.info('Downloaded document', { documentId: doc.document_id });
    } catch (error) {
      log.error('Error downloading document', {
        error: error instanceof Error ? error.message : String(error),
        documentId: doc.document_id || 'unknown'
      });
      onShowSnackbar(t('documents.notification.downloadError'), 'error');
    }
  }, [onShowSnackbar, t]);

  return {
    handleView,
    handleDownload
  };
};
